import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchWaConversations, setWaFilter, setWaSearch, setWaTag, setWaActive,
} from "../../../Slices/whatsappSlice";
import { WA_FILTERS, displayNameOf, initialsOf, listTime, isWindowOpen } from "./waUtils";

const SEARCH_DEBOUNCE_MS = 300;

const SkeletonRows = () =>
  Array.from({ length: 7 }).map((_, i) => (
    <div className="wa-row" key={i} aria-hidden="true">
      <div className="wa-avatar wa-skeleton" />
      <div className="wa-row__main">
        <div className="wa-skeleton" style={{ height: 12, width: "60%", marginBottom: 8 }} />
        <div className="wa-skeleton" style={{ height: 10, width: "85%" }} />
      </div>
    </div>
  ));

const ConversationList = ({ searchInputRef, onNewMessage, tags, now }) => {
  const dispatch = useDispatch();
  const { filter, search, tag, list, activeId, status } = useSelector((s) => s.whatsapp);
  const counts = status?.counts || {};
  const userId = status?.userId;
  const [searchText, setSearchText] = useState(search);
  const debounceRef = useRef(null);

  // Refetch when tab / search / tag changes.
  useEffect(() => {
    dispatch(fetchWaConversations());
  }, [dispatch, filter, search, tag]);

  const onSearchChange = (value) => {
    setSearchText(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => dispatch(setWaSearch(value.trim())), SEARCH_DEBOUNCE_MS);
  };
  useEffect(() => () => clearTimeout(debounceRef.current), []);

  const onListKeyDown = (e) => {
    if (!["ArrowDown", "ArrowUp"].includes(e.key) || !list.items.length) return;
    e.preventDefault();
    const idx = list.items.findIndex((c) => c.id === activeId);
    const next = e.key === "ArrowDown" ? Math.min(list.items.length - 1, idx + 1) : Math.max(0, idx - 1);
    dispatch(setWaActive(list.items[next].id));
  };

  return (
    <div className="wa-list" role="region" aria-label="Conversations">
      <div className="wa-list__head">
        <div className="wa-list__title">
          <h2>
            <i className="fa-brands fa-whatsapp" style={{ color: "var(--wa-accent)" }} /> WhatsApp
            {status?.displayNumber && <small>{status.displayNumber}</small>}
          </h2>
          <button type="button" className="wa-btn wa-btn--primary wa-btn--sm" onClick={onNewMessage} title="Start a conversation with a template">
            <i className="fa-solid fa-plus" /> New
          </button>
        </div>
        <div className="wa-search">
          <i className="fa-solid fa-magnifying-glass" />
          <input
            ref={searchInputRef}
            className="wa-input"
            placeholder="Search name, number or tag"
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Search conversations"
          />
          {!searchText && <span className="wa-kbd">/</span>}
        </div>
      </div>

      <nav className="wa-tabs" role="tablist" aria-label="Conversation filter">
        {WA_FILTERS.map((f) => {
          const unread = f.unreadKey ? counts[f.unreadKey] || 0 : 0;
          const count = counts[f.countKey];
          return (
            <button
              key={f.key}
              type="button"
              role="tab"
              aria-selected={filter === f.key}
              className={`wa-tab ${filter === f.key ? "wa-tab--active" : ""}`}
              onClick={() => dispatch(setWaFilter(f.key))}
            >
              {f.label}
              {unread > 0 ? (
                <span className="wa-tab__count wa-tab__count--unread" title={`${unread} unread`}>{unread}</span>
              ) : count > 0 && f.key !== "closed" ? (
                <span className="wa-tab__count">{count}</span>
              ) : null}
            </button>
          );
        })}
      </nav>

      {tags?.length > 0 && (
        <div className="wa-list__filters">
          <select className="wa-select" value={tag} onChange={(e) => dispatch(setWaTag(e.target.value))} aria-label="Filter by tag">
            <option value="">All tags</option>
            {tags.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
      )}

      <div className="wa-list__body" role="listbox" aria-label="Conversation list" tabIndex={0} onKeyDown={onListKeyDown}>
        {list.loading && !list.items.length && <SkeletonRows />}
        {list.error && !list.items.length && (
          <div className="wa-empty">
            <i className="fa-solid fa-triangle-exclamation wa-empty__icon" />
            <div className="wa-empty__title">Couldn't load conversations</div>
            <div>{list.error}</div>
            <button type="button" className="wa-btn wa-btn--sm" onClick={() => dispatch(fetchWaConversations())}>Retry</button>
          </div>
        )}
        {!list.loading && !list.error && !list.items.length && (
          <div className="wa-empty">
            <i className="fa-regular fa-comments wa-empty__icon" />
            <div className="wa-empty__title">{search ? "No matches" : filter === "mine" ? "Nothing assigned to you" : filter === "unassigned" ? "Inbox zero" : "No conversations"}</div>
            <div>{search ? "Try a different name or number." : "New WhatsApp messages appear here in real time."}</div>
          </div>
        )}
        {list.items.map((c) => {
          const name = displayNameOf(c);
          const open = isWindowOpen(c, now);
          const isActive = c.id === activeId;
          return (
            <button
              type="button"
              key={c.id}
              role="option"
              aria-selected={isActive}
              className={`wa-row ${isActive ? "wa-row--active" : ""} ${c.unreadCount > 0 ? "wa-row--unread" : ""}`}
              onClick={() => dispatch(setWaActive(c.id))}
            >
              <div className="wa-avatar">{initialsOf(name, "#")}</div>
              <div className="wa-row__main">
                <div className="wa-row__top">
                  <span className="wa-row__name" title={name}>{name}</span>
                  <span className="wa-row__time">{listTime(c.lastMessageAt || c.createdAt)}</span>
                </div>
                <div className="wa-row__preview">
                  <span className={`wa-dot ${open ? "wa-dot--open" : ""}`} title={open ? "Reply window open" : "Reply window closed — templates only"} />
                  <span className="wa-row__text">
                    {c.lastMessageDirection === "outbound" && <i className="fa-solid fa-reply" style={{ fontSize: 10, marginRight: 4 }} />}
                    {c.lastMessagePreview || "No messages yet"}
                  </span>
                  {c.unreadCount > 0 && <span className="wa-unread">{c.unreadCount > 99 ? "99+" : c.unreadCount}</span>}
                </div>
                <div className="wa-row__meta">
                  {c.assignedTo ? (
                    <span className={`wa-chip ${c.assignedTo === userId ? "wa-chip--accent" : ""}`}>
                      <i className="fa-solid fa-user" style={{ fontSize: 9 }} /> {c.assignedTo === userId ? "You" : c.assignedToName || "Assigned"}
                    </span>
                  ) : (
                    <span className="wa-chip wa-chip--warn">Unassigned</span>
                  )}
                  {c.status === "pending" && <span className="wa-chip">Pending</span>}
                  {c.leadId && <span className="wa-chip" title="Linked to a lead"><i className="fa-solid fa-link" style={{ fontSize: 9 }} /> Lead</span>}
                  {c.clientId && <span className="wa-chip" title="Linked to a client"><i className="fa-solid fa-link" style={{ fontSize: 9 }} /> Client</span>}
                  {(c.tags || []).slice(0, 2).map((t) => <span key={t} className="wa-chip">{t}</span>)}
                </div>
              </div>
            </button>
          );
        })}
        {list.hasMore && (
          <div className="wa-list__more">
            <button type="button" className="wa-btn wa-btn--sm" disabled={list.loading} onClick={() => dispatch(fetchWaConversations({ append: true }))}>
              {list.loading ? "Loading…" : "Load more"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationList;
